import { Component, OnInit, AfterViewInit, ChangeDetectorRef, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';
import { ApiService, JournalGroup, BookGroup, Summary, SliderItem, SlideItem } from '../../services/api.service';
import { forkJoin } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { LanguageService } from '@/app/services/language.service';
import { ImageCacheService } from '@/app/services/image-cache.service';

@Component({
    selector: 'app-home',
    templateUrl: './home.component.html',
    styleUrls: ['./home.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class HomeComponent implements OnInit, AfterViewInit {
  journals: JournalGroup[] = [];
  books: BookGroup[] = [];
  summary: Summary = { journals: 0, books: 0, articles: 0, authors: 0 };
  sliderImages: any[] = [];
  loading = true;
  selectedJournalTabIndex = 0;
  selectedBookTabIndex = 0;

  // Properties instead of getters
  journalTabs: string[] = [];
  bookTabs: string[] = [];
  currentJournalGroup: JournalGroup | null = null;
  currentBookGroup: BookGroup | null = null;

  // Search properties
  quickSearchQuery = '';
  showAdvancedSearch = false;
  searchScope = 0;

  toggleAdvancedSearch(): void {
    this.showAdvancedSearch = !this.showAdvancedSearch;
  }

  constructor(private apiService: ApiService,
    private translate: TranslateService,
    private languageService: LanguageService,
    private cdr: ChangeDetectorRef,
    private imageCacheService: ImageCacheService,
    private router: Router) { }

  ngOnInit(): void {
    this.loadData();
  }

  selectJournalTab(index: number): void {
    this.selectedJournalTabIndex = index;
    this.updateCurrentGroups();
  }

  selectBookTab(index: number): void {
    this.selectedBookTabIndex = index;
    this.updateCurrentGroups();
  }

  getJournalTabs(): string[] {
    return this.journals.map(group => {
      // Grup adını al
      const groupName = group.name || '';

      // Boşlukları çıkar ve başına @ ekle
      const translationKey = '@' + groupName.replace(/\s+/g, '');

      // Çeviri dosyasından çeviriyi al
      const translatedName = this.translate.instant(translationKey);

      // Eğer çeviri bulunamazsa (key aynen dönerse) orijinal ismi kullan
      return translatedName === translationKey ? groupName : translatedName;
    });
  }

  getBookTabs(): string[] {
    return this.books.map(group => {
      // Grup adını al
      const groupName = group.name || '';

      // Boşlukları çıkar ve başına @ ekle
      const translationKey = '@' + groupName.replace(/\s+/g, '');

      // Çeviri dosyasından çeviriyi al
      const translatedName = this.translate.instant(translationKey);

      // Eğer çeviri bulunamazsa (key aynen dönerse) orijinal ismi kullan
      return translatedName === translationKey ? groupName : translatedName;
    });
  }


  private updateTabsAndCurrentGroups(): void {
    // Update tabs
    this.journalTabs = this.journals.map(group => group.name);
    this.bookTabs = this.books.map(group => group.name);

    // Set initial selections and update current groups
    this.selectedJournalTabIndex = 0;
    this.selectedBookTabIndex = 0;
    this.updateCurrentGroups();
  }

  private updateCurrentGroups(): void {
    this.currentJournalGroup = this.journals[this.selectedJournalTabIndex] || null;
    this.currentBookGroup = this.books[this.selectedBookTabIndex] || null;
  }

  ngAfterViewInit(): void {
    // Initialize any jQuery plugins if needed
    this.initializePlugins();
  }

  private loadData(): void {
    this.loading = true;

    // Load journals, books, and summary
    forkJoin({
      journals: this.apiService.getAllJournals(),
      books: this.apiService.getAllBooks(),
      summary: this.apiService.getSummary()
    }).subscribe({
      next: (result) => {
        this.journals = result.journals || [];
        this.books = result.books || [];

        // Tabs ve current groups'u güncelle
        this.updateTabsAndCurrentGroups();

        this.summary = {
          journals: Number(result.summary.journals) || 0,
          books: Number(result.summary.books) || 0,
          articles: Number(result.summary.articles) || 0,
          authors: Number(result.summary.authors) || 0
        };

        // Slider'ları yükle
        this.loadSliderImages();
      },
      error: (error) => {
        console.error('HomeComponent: Error loading data:', error);
        this.loading = false;
      }
    });
  }

  private loadSliderImages(): void {
    this.apiService.getSliderItemsList().subscribe(
      (data: SliderItem[]) => {
        // Slider görsellerini hazırla, cache directive halledicek
        this.sliderImages = data.map(item => ({
          IMAGE: `https://cdn.istanbul.edu.tr/image/JTA6CLJ8T5/${item.IMAGE}`,
          LINK: item.LINK || '#',
          ALT: item.ALT || 'Slider Image',
          ID: item.ID,
          RANK: item.RANK,
          ISACTIVE: item.ISACTIVE,
          CREATEDDATE: item.CREATEDDATE,
          CREATEDUSER: item.CREATEDUSER,
          ISDELETED: item.ISDELETED,
          MODIFIEDDATE: item.MODIFIEDDATE,
          MODIFIEDUSER: item.MODIFIEDUSER
        }));

        this.loading = false;
        this.cdr.detectChanges();
      },
      error => {
        console.error('HomeComponent: Error loading slider images:', error);
        // API hata verirse varsayılan görselleri kullan
        this.sliderImages = [
          {
            IMAGE: 'https://via.placeholder.com/800x400/0066cc/ffffff?text=İstanbul+Üniversitesi+Press',
            LINK: '#',
            ALT: 'İstanbul Üniversitesi Press'
          },
          {
            IMAGE: 'https://via.placeholder.com/800x400/006633/ffffff?text=Akademik+Yayınlar',
            LINK: '#',
            ALT: 'Akademik Yayınlar'
          }
        ] as SliderItem[];
        this.loading = false;
      }
    );
  }

  private initializePlugins(): void {
    // Initialize any third-party plugins here
    // This replaces the jQuery initialization from the React component
  }

  performQuickSearch(): void {
    if (this.quickSearchQuery.trim()) {
      this.router.navigate(['/search/results'], {
        queryParams: {
          q: this.quickSearchQuery,
          type: 'quick',
          scope: this.searchScope
        }
      });
    }
  }
}